import { Link } from "react-router-dom";
import { FaLinkedin, FaFacebookF, FaInstagram, FaTwitter } from "react-icons/fa";
import { Mail, Phone, MapPin, Heart, Globe, Shield, Zap } from 'lucide-react';

export default function Footer() {
  const socialLinks = [
    {
      href: "https://www.facebook.com/communiticate/",
      icon: FaFacebookF,
      label: "Facebook"
    },
    {
      href: "https://www.instagram.com/communiticate/",
      icon: FaInstagram,
      label: "Instagram"
    },
    {
      href: "https://www.linkedin.com/company/communiticate/",
      icon: FaLinkedin,
      label: "LinkedIn"
    },
    {
      href: "https://twitter.com/communiticate",
      icon: FaTwitter,
      label: "Twitter"
    }
  ];

  const quickLinks = [
    { href: '#whyChoose', label: 'Why Choose Us' },
    { href: '#whatMakes', label: 'Features' },
    { href: '#coreFeatures', label: 'How It Works' },
    { href: '#contactUs', label: 'Get Started' }
  ];

  const resources = [
    { href: '/blog', label: 'Community Blog' },
    { href: '/help', label: 'Help Center' },
    { href: '/templates', label: 'Community Templates' },
    { href: '/case-studies', label: 'Success Stories' }
  ];

  const legal = [
    { href: '/privacy', label: 'Privacy Policy' },
    { href: '/terms', label: 'Terms of Service' },
    { href: '/security', label: 'Security' },
    { href: '/cookies', label: 'Cookie Policy' }
  ];

  return (
    <footer className="bg-gradient-to-r from-gray-900 via-purple-900 to-blue-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-blue-400 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">C</span>
              </div>
              <span className="text-2xl font-bold">Communiticate</span>
            </div>
            
            <p className="text-white/80 leading-relaxed max-w-md">
              Empowering community builders worldwide to create meaningful connections and foster 
              collaboration. Build, grow, and manage thriving communities with ease.
            </p>
            
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-purple-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap gap-4 text-sm text-white/60">
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4 text-green-400" />
                <span>SOC 2 Certified</span>
              </div>
              <div className="flex items-center gap-1">
                <Globe className="w-4 h-4 text-blue-400" />
                <span>GDPR Ready</span>
              </div>
              <div className="flex items-center gap-1">
                <Zap className="w-4 h-4 text-yellow-400" />
                <span>99.9% Uptime</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Quick Links</h3>
            <div className="space-y-3">
              {quickLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="block text-white/70 hover:text-purple-400 transition-colors duration-200 text-sm"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          {/* Resources */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Resources</h3>
            <div className="space-y-3">
              {resources.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="block text-white/70 hover:text-purple-400 transition-colors duration-200 text-sm"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Legal */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Legal & Support</h3>
            <div className="space-y-3">
              {legal.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="block text-white/70 hover:text-purple-400 transition-colors duration-200 text-sm"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            
            {/* Contact info */}
            <div className="pt-4 space-y-2">
              <a
                href="mailto:hello@communiticate.com"
                className="flex items-center gap-2 text-white/70 hover:text-purple-400 transition-colors duration-200 text-sm"
              >
                <Mail className="w-4 h-4" />
                hello@communiticate.com
              </a>
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <Globe className="w-4 h-4" />
                Global Community Platform
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="bg-white/5 rounded-2xl p-6 mb-8">
            <div className="text-center max-w-2xl mx-auto">
              <h4 className="text-xl font-semibold mb-2">Stay Connected</h4>
              <p className="text-white/70 mb-4 text-sm">
                Get the latest community building tips, product updates, and success stories delivered to your inbox.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                />
                <button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-blue-600 hover:to-purple-600 px-6 py-3 rounded-lg font-semibold transition-all duration-300 text-sm whitespace-nowrap">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-sm text-center lg:text-left">
              Copyright © 2025 Communiticate. All rights reserved. Building communities that matter.
            </p>
            
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-white/70">
                <span>Made with</span>
                <Heart className="w-4 h-4 text-red-400" />
                <span>for community builders</span>
              </div>
              
              <div className="flex items-center gap-1 text-white/50">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>All systems operational</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
